/**
 * Pure helpers over the context payload of the signed in user (`contexts`, `active_context`,
 * `context_modes`), shared by the context store, useContext(), the route guard and the sidebar.
 * They read both shapes: the one of HasContexts, whose entries carry `context`, and the older
 * one of HasTeamContext/HasEventContext/HasEventTeamContext, whose entries do not.
 */
import type { ActiveContextItem, AuthUser, ContextEntry, ContextMode } from '@lib/types';

/** How a route or a sidebar entry wants a context: one record, "all", or either of them. */
export type ContextScope = 'single' | 'all' | 'any';

/** The parts of the user the helpers read. */
export type ContextSource = Partial<Pick<AuthUser, 'contexts' | 'active_context' | 'context_modes' | 'permissions'>>;

/** Id of the "all" entry. */
export const ALL_ID = 'all';

/** Class basename, lower case: `App\Models\Team` -> `team`. */
function basename(name?: string | null): string {
	return (name ?? '').replace(/^.*[\\/]/, '').toLowerCase();
}

/** Key of a kind from a segment, class or key: `teams`, `Team`, `App\Models\Team` -> `team`. */
export function singularKey(key: string): string {
	const base = basename(key);
	return base.endsWith('s') ? base.slice(0, -1) : base;
}

/** Key of a kind of the payload, derived where the old payload has none. */
export function contextKeyOf(entry: Pick<ContextEntry, 'key' | 'context' | 'model'>): string {
	if (entry.context) return entry.context;
	return entry.key ? singularKey(entry.key) : basename(entry.model);
}

/** Whether the payload comes from the old user traits, which carry no `context` per entry. */
export function isLegacyPayload(contexts?: ContextEntry[] | null): boolean {
	return !!contexts?.length && contexts.every((entry) => entry.context === undefined);
}

/** The key of a kind for any of its names: key, segment, class or basename, case-insensitive. */
export function normalizeContextKey(key: string, contexts?: ContextEntry[] | null): string {
	const lower = key.toLowerCase();
	const base = basename(key);
	const entry = contexts?.find(
		(e) =>
			contextKeyOf(e) === base ||
			e.key?.toLowerCase() === lower ||
			e.key?.toLowerCase() === base ||
			basename(e.model) === base,
	);
	return entry ? contextKeyOf(entry) : singularKey(key);
}

/** The kind of the payload for a key, if the user has it. */
export function contextEntry(source: ContextSource, key: string): ContextEntry | undefined {
	const k = normalizeContextKey(key, source.contexts);
	return source.contexts?.find((e) => contextKeyOf(e) === k);
}

/** The active entry of a kind, matched by key, else by position, else by class. */
export function activeEntry(source: ContextSource, key: string): ActiveContextItem | undefined {
	const contexts = source.contexts ?? [];
	const active = source.active_context ?? [];
	const k = normalizeContextKey(key, contexts);

	const byKey = active.find((a) => a.context === k);
	if (byKey) return byKey;

	const ind = contexts.findIndex((e) => contextKeyOf(e) === k);
	if (ind >= 0 && active[ind] && active[ind].context === undefined) return active[ind];

	return active.find((a) => a.context === undefined && basename(a.model) === k);
}

/** Id of the active record of a kind as a string, `'all'`, or null. */
export function activeId(source: ContextSource, key: string): string | null {
	const id = activeEntry(source, key)?.id;
	return id === null || id === undefined || id === '' ? null : String(id);
}

/** Whether the kind is set to "all". */
export function isAllActive(source: ContextSource, key: string): boolean {
	const entry = activeEntry(source, key);
	return !!entry && (entry.is_all === true || String(entry.id) === ALL_ID);
}

/** URL segment of a kind: from the payload, else the plural of the key. */
export function contextSegment(source: ContextSource, key: string): string {
	const entry = contextEntry(source, key);
	if (entry?.key) return entry.key;
	return activeEntry(source, key)?.key ?? `${normalizeContextKey(key, source.contexts)}s`;
}

/** `<segment>/<id>` of a record of a kind, '' without an id or for "all". */
export function recordPath(source: ContextSource, key: string, id?: string | number | null): string {
	if (id === null || id === undefined || id === '' || String(id) === ALL_ID) return '';
	return `${contextSegment(source, key)}/${id}`;
}

/** `<segment>/<id>` of the active record of a kind, '' for "all" or none. */
export function activePath(source: ContextSource, key: string): string {
	return isAllActive(source, key) ? '' : recordPath(source, key, activeId(source, key));
}

/** The API prefix of the whole selection, parents first; a kind set to "all" or none is left out. */
export function contextPath(source: ContextSource): string {
	return (source.contexts ?? [])
		.map((entry) => activePath(source, contextKeyOf(entry)))
		.filter(Boolean)
		.join('/');
}

/** Whether the selection of a kind is what `scope` asks for; a kind without a choice never is. */
export function scopeMatches(source: ContextSource, key: string, scope: ContextScope): boolean {
	if (activeId(source, key) === null) return false;
	if (scope === 'any') return true;
	return isAllActive(source, key) === (scope === 'all');
}

/** Whether the active record of a kind grants the context permission (the global ones on "all"). */
export function hasActiveContextPermission(source: ContextSource, key: string, permission: string): boolean {
	const entry = activeEntry(source, key);

	return !!entry && activeId(source, key) !== null && !!entry.context_permissions?.includes(permission);
}

/**
 * Whether the user may do `permission` in a kind: a context permission on its active record
 * (the global ones on "all"), else the global permission, else `context.bypass`.
 */
export function contextCan(source: ContextSource, key: string, permission: string): boolean {
	if (hasActiveContextPermission(source, key, permission)) return true;

	const global = source.permissions ?? [];
	return global.includes(permission) || global.includes('context.bypass');
}

/** A mode of the user's contexts by name. */
export function contextMode(source: ContextSource, name: string): ContextMode | undefined {
	return source.context_modes?.find((mode) => mode.name === name);
}

/** The context and mode requirements of a route or a sidebar entry. */
export type ContextRequirement = {
	/** Per context key the selection it needs. */
	context?: Record<string, ContextScope>;
	/** Per mode name the values it is shown for. */
	mode?: Record<string, string[]>;
};

/** Whether the selection meets the requirements; none always do. */
export function requirementMet(source: ContextSource, requirement: ContextRequirement): boolean {
	const contexts = Object.entries(requirement.context ?? {});
	const modes = Object.entries(requirement.mode ?? {});

	return (
		contexts.every(([key, scope]) => scopeMatches(source, key, scope)) &&
		modes.every(([name, values]) => {
			const value = contextMode(source, name)?.value;
			return value !== null && value !== undefined && values.includes(value);
		})
	);
}
